import type {
  Dieseltank,
  Fardkort,
  Motorinstallningar,
  Motorinstanser,
  Sammandragsdata,
} from "./fard.ts";
import type { Formagekoppling, Hemlighetslada, Homeyinstallningar, Homeyorder } from "./homey.ts";
import type { Forbrukarnyckel, STYRMAL, Varde } from "./signaler.ts";
import type { Uppstallningsforbrukare, Utblas } from "./uppstallning.ts";
import type { Nyckellada, Victroninstallningar, Victronorder } from "./victron.ts";

/** Notisernas nivåer (kravspecen §7). */
export type Niva = "info" | "varning" | "akut";

export const NIVAER: readonly Niva[] = ["info", "varning", "akut"];

export function hogstaNiva(a: Niva, b: Niva): Niva {
  return NIVAER.indexOf(a) >= NIVAER.indexOf(b) ? a : b;
}

/**
 * En mätpunkt från navet. `id` är unikt för varje mätpunkt och gör mottagningen idempotent.
 * `seq` är buffertens löpnummer i navet och visar ordningen.
 */
export interface Matpunkt {
  typ: "matpunkt";
  id: string;
  seq: number;
  signal: string;
  instans: string | null;
  varde: Varde;
  /** Tiden då värdet lästes, i millisekunder sedan 1970 (UTC). */
  tid: number;
}

/** En händelse som navet själv har skapat, till exempel från nödreglerna. */
export interface NavHandelse {
  typ: "handelse";
  id: string;
  seq: number;
  nyckel: string;
  niva: Niva;
  text: string;
  tid: number;
}

/**
 * b12: ett sammandrag som navet har gjort av NMEA 2000: en resa (medan den pågår och när den
 * slutar) eller en minut under en resa. Medelvärden och summor, inga råa ramar och inget spår.
 */
export type Sammandrag = {
  typ: "sammandrag";
  id: string;
  seq: number;
  tid: number;
} & Sammandragsdata;

export type Post = Matpunkt | NavHandelse | Sammandrag;

/** Det navet skickar till molnet. */
export interface Inleverans {
  poster: Post[];
  /**
   * b16: navets publika nyckel för den förseglade lådan (lada.ts), och lådorna som navet har tagit
   * emot sedan förra gången. Ett nav utan Bluetooth har ingen.
   */
  lada?: { publik: string; mottagna: string[] };
}

/** b16: värmarens signal ur utblåset, till navet: jämförelsen och gränserna. */
export type Utblasregel = Omit<Utblas, "givare"> & {
  /** En entitet i Home Assistant (ha:sensor.…), som källan för Home Assistant följer. Annars null. */
  ha: string | null;
};

/** Molnets svar på en inleverans. */
export interface Kvitto {
  antal: number;
  nya: number;
  dubbletter: number;
  /**
   * Kommandon som navet ska utföra (b3 avsnitt 3 E). De kommer bara när platsens styrning är på,
   * och aldrig till en båt. Ett nav som inte känner till fältet bryr sig inte om det.
   */
  kommandon?: Kommando[];
  /**
   * b10: vinterläget, bara till ett nav ombord. Ett nav utan MasterBus, eller som inte känner
   * till fältet, bryr sig inte om det.
   */
  vinterlage?: Vinterorder;
  /**
   * b12: vilken instans i NMEA 2000 som är vilken motor, bara till ett nav ombord och bara när
   * den inte är förvalet. Utan fältet gäller förvalet (0 babord, 1 styrbord).
   */
  motorer?: Motorinstanser;
  /**
   * b15: förbrukarnas signaler, bara när någon har en: värmaren och signalen för när den går, till
   * exempel {varmare: "ha:switch.varmaren"}. Navet räknar timmarna som förbrukaren går.
   */
  forbrukare?: Partial<Record<Forbrukarnyckel, string>>;
  /** b16: värmarna vars signal härleds ur utblåset, bara när någon har det. */
  utblas?: Partial<Record<Forbrukarnyckel, Utblasregel>>;
  /** b16: Victron, bara när platsen har en GX-enhet eller en bank för en Victron-enhet. */
  victron?: Victronorder;
  /** b16: nycklarna i förseglade lådor till det här navet, tills navet har kvitterat dem. */
  nycklar?: Nyckellada[];
  /** b17: Homey, bara när platsen har en adress för Homey. */
  homey?: Homeyorder;
  /**
   * b17: förmågorna som ägaren har kopplat till Kolls signaler i guiden, bara när det finns några.
   * Utan fältet: inga.
   */
  kopplingar?: Formagekoppling[];
  /** b17: GX-enhetens lösenord och Homeys nyckel i förseglade lådor, tills navet har kvitterat. */
  hemligheter?: Hemlighetslada[];
}

/** b10: vinterläget som molnet säger det till navet ombord, i varje svar. */
export interface Vinterorder {
  /** Styrningen i molnet, den första av de tre spärrarna. */
  molnet: boolean;
  /** Fas 2, det automatiska vinterläget, är på i molnet. */
  pa: boolean;
  /**
   * När fas 2 senast slogs på, i millisekunder sedan 1970. Har navet slagit av fas 2 (till
   * exempel efter ett fel), gäller det tills den här tiden ändras.
   */
  sedan: number | null;
  /** Laddaren av när banken har så här många procent eller mer. */
  avProcent: number;
  /** Laddaren på igen när banken har så här många procent eller mindre. */
  paProcent: number;
  /**
   * b11 (BK33): styrningen i molnet stängs. Navet får då bara den säkra återgången: skriva 1
   * (laddaren på) där värdet inte redan är 1, och läsa tillbaka. Ett nav från b10 bryr sig
   * inte om fältet, och ser bara att styrningen är av.
   */
  stanger?: boolean;
}

/**
 * Vad ett kommando styr: Nobø-hubbens globala läge, en scen eller ett skript i HA, eller (b10)
 * en laddare i vinterläget via MasterBus.
 */
export type Kommandomal = (typeof STYRMAL)[number];

/** Ett kommando från molnet till navet. */
export interface Kommando {
  id: string;
  mal: Kommandomal;
  /**
   * För Nobø: normal, komfort, eko eller borta. För Home Assistant: forvarm, hemma eller borta,
   * alltså scenen eller skriptet för förvärmningen, ankomsten och avresan (b3-1). För
   * MasterBus (b10): raden i vitlistan och värdet, till exempel masscombi:0 (vinter.ts).
   */
  varde: string;
  /** När kommandot ska utföras, i millisekunder sedan 1970 (UTC). */
  tid: number;
}

/** Navets svar på ett kommando: om det gick, vad som hände och vad som lästes tillbaka. */
export interface Kommandoresultat {
  ok: boolean;
  resultat: string;
  tillbakalast: string | null;
  /**
   * Varför navet inte gjorde något (b3-1): styrningen är av i navets inställningar på platsen
   * ("sparrat"), eller ingen scen är vald för det här i navet ("ingen_scen").
   */
  gjordesInte?: "sparrat" | "ingen_scen";
}

/**
 * Båda spärrarna för en plats (b3-1): molnets, som ägaren slår på med skriptet, och platsens, i
 * navets egna inställningar. Navet gör något bara när båda är på.
 */
export interface Styrningslage {
  molnet: boolean;
  /** Platsens spärr per nav som styr, som navet senast sade den. null: navet har inte sagt något. */
  platsen: { mal: Kommandomal; pa: boolean | null }[];
  /** Till exempel "molnet på, platsen av". Appen skriver "Styrning:" före. */
  text: string;
  /**
   * b11 (BK33): en båt vars styrning i molnet stängs, sedan den här tiden. Navet slår på
   * laddarna först, och svarar, eller så går styrningen till av efter 10 minuter. Null annars.
   */
  stanger?: number | null;
}

export type Svar = "normalt" | "bra_att_veta";

/** En händelse som appen visar. */
export interface Handelse {
  id: string;
  nyckel: string;
  niva: Niva;
  text: string;
  tid: number;
  /** När villkoret upphörde, eller null om det pågår. */
  avslutad: number | null;
  kalla: "moln" | "nav";
  /** Navet larmade också lokalt (nödregel). */
  navLarmade: boolean;
  /** Händelsen kom fram i efterhand, när navets buffert kom fram efter ett avbrott. */
  iEfterhand: boolean;
  svar: Svar | null;
  /**
   * b15: en åtgärd i appen, till exempel "Lägga till?" för en ny givare, som öppnar guiden. Utan
   * fältet har händelsen ingen.
   */
  atgard?: { text: string; vy: "kom-igang" };
}

export interface Signalvarde {
  varde: Varde;
  tid: number;
}

export type Generatorlage = "gar" | "stoppad" | "avstangd" | "fel" | "okant";

/**
 * Platsens typ (b3 avsnitt 3 A). Reglerna följer typen. b15: husbilen, som har husens regler, och
 * vars produkt är Husbilskoll (katalogen, BK25).
 */
export type Platstyp = "bat" | "hus" | "fritidshus" | "husbil" | "annat";

export const PLATSTYPER: readonly Platstyp[] = ["bat", "hus", "fritidshus", "husbil", "annat"];

/** Ett kontos roll för en plats. */
export type Roll = "agare" | "lasare";

export const ROLLER: readonly Roll[] = ["agare", "lasare"];

/** Var navet sitter: ombord, i ett hus eller inne i Home Assistant. */
export type Navsort = "ombord" | "hus" | "ha";

export const NAVSORTER: readonly Navsort[] = ["ombord", "hus", "ha"];

export interface Platsinfo {
  id: string;
  namn: string;
  typ: Platstyp;
  tidszon: string;
  roll: Roll;
  /** Styrningen av Home Assistant och Nobø är på. Den är av tills en ägare slår på den. */
  styrning: boolean;
  /**
   * Platsen är i drift (b6). Ej i drift tystar bara varningarna om att navet eller en enhet har
   * varit tyst. Akut och allt annat som navet skickar bedöms som vanligt.
   */
  iDrift: boolean;
}

/**
 * Platsens status på startsidan: rött vid Akut, gult vid Varning eller ett tyst nav, och grått
 * för en plats som är ej i drift (b6) när inget annat pågår.
 */
export type Platsstatus = "gron" | "gul" | "rod" | "gra";

/** Ett kort på startsidan. */
export interface Platskort extends Platsinfo {
  status: Platsstatus;
  /** Varför kortet är gult eller rött, eller tom. */
  statusText: string;
  navSenastHord: number | null;
  /**
   * Två eller tre nyckelvärden efter platsens typ. b20-1: källan i liten text (för Ute, till
   * exempel "Ecowitt GW2000 via Home Assistant"), och om värdet är gammalt.
   */
  varden: { namn: string; text: string; kalla?: string; gammalt?: boolean }[];
  senasteHandelse: { niva: Niva; text: string; tid: number; pagar: boolean } | null;
  /** Planerna för "Åka till" och "Resa bort", som text. */
  planer: Planvisning[];
  /** Båda spärrarna, eller null för en båt. */
  styrningslage: Styrningslage | null;
  /**
   * Platsen är ej i drift, men navet har hörts av sedan den sattes så (b6). Kortet visar det,
   * med en knapp för att ta platsen i drift.
   */
  hordUtanDrift: boolean;
  /**
   * b11: platsen är ej i drift och navet har varit tyst längre än gränsen för tyst nav. Värdena
   * bedöms inte, och kortet visar dem som de senast kända, från den här tiden. Null annars.
   */
  senastKanda?: number | null;
}

/** Startsidan: den gemensamma rapporten och ett kort per plats. */
export interface Oversikt {
  rapport: string[];
  platser: Platskort[];
}

export type Plantyp = "aka_till" | "resa_bort";

/** Elområdena för day-ahead-priserna (b4 avsnitt 3 A). */
export const ELOMRADEN = [
  "SE1",
  "SE2",
  "SE3",
  "SE4",
  "NO1",
  "NO2",
  "NO3",
  "NO4",
  "NO5",
  "ES",
] as const;

export type Elomrade = (typeof ELOMRADEN)[number];

/** Elavtalet: spotpris per timme eller kvart, eller ett fast pris. */
export const ELAVTALEN = ["timpris", "fast"] as const;

export type Elavtal = (typeof ELAVTALEN)[number];

/**
 * Nättariffen (b6): "ingen" är en enda period, som förut. "ES 2.0TD" är den spanska tariffen
 * med perioderna P1, P2 och P3, där nätavgiften beror på tiden. Den gäller bara elområdet ES.
 */
export const NATTARIFFER = ["ingen", "ES 2.0TD"] as const;

export type Nattariff = (typeof NATTARIFFER)[number];

/** Perioderna i ES 2.0TD: P1 (punta), P2 (llano) och P3 (valle). */
export const PERIODER = ["P1", "P2", "P3"] as const;

export type Period = (typeof PERIODER)[number];

/** Elen och värmemodellen för ett hus, på Hälsa (b4). */
export interface Elvisning {
  /** Till exempel "SE3, timpris, påslag 85 öre/kWh". */
  text: string;
  /** Värmemodellens parametrar och om de är förval, skattade eller inställda. */
  modell: string;
}

/** En plan för en plats, med det som görs, som text. */
export interface Planvisning {
  id: string;
  typ: Plantyp;
  /** Ankomsten (Åka till) eller början på perioden (Resa bort). */
  fran: number;
  /** Avresan (Åka till, får saknas) eller slutet på perioden (Resa bort). */
  till: number | null;
  rader: string[];
  /**
   * Förvärmningens beräknade kostnad i platsens valuta, så länge något av förvärmningen
   * återstår (b5). Den gemensamma rapporten räknar ihop den i kronor.
   */
  kostnad?: { belopp: number; valuta: "SEK" | "NOK" | "EUR" } | null;
}

/** En ändring av en plats, i platsens logg (b5): vem, när och vad, från och till. */
export interface Andringsrad {
  tid: number;
  vem: string;
  text: string;
}

/** Var en parameter i värmemodellen kommer från. */
export type Parameterkalla = "förval" | "skattad" | "inställd";

/** En parameter i värmemodellen, under Avancerat (b5). */
export interface Modellparameter {
  /** effektKw (P), forlustKwPerGrad (UA) eller kapacitetKwhPerGrad (C). */
  nyckel: "effektKw" | "forlustKwPerGrad" | "kapacitetKwhPerGrad";
  /** Till exempel "Effekt (P)". */
  namn: string;
  enhet: string;
  varde: number;
  kalla: Parameterkalla;
  forval: number;
  min: number;
  max: number;
}

/** En plats inställningar, som bara ägarna ser och ändrar (b5). */
export interface Platsinstallningar {
  /** b15: guiden "Kom igång": när den kördes klart (null: inte än), och delningen av okända enheter. */
  guide: { klar: number | null; delaOkanda: boolean };
  plats: Platsinfo & { position: { lat: number; lon: number } | null };
  /** Elen, eller null för en båt. */
  el: {
    elomrade: Elomrade | null;
    /** Förvalet efter tidszonen, när elområdet inte är ifyllt. */
    forval: Elomrade | null;
    elavtal: Elavtal | null;
    paslag: number | null;
    /** "öre/kWh" eller "cent/kWh", efter elområdet. */
    paslagsenhet: string;
    valuta: "SEK" | "NOK" | "EUR";
    /** Molnet har elpriser för området de senaste två dygnen. */
    priser: boolean;
    /** Nättariffen (b6), och de som går att välja för elområdet. */
    nattariff: Nattariff;
    nattariffer: Nattariff[];
    /** Påslaget per period med ES 2.0TD, i samma enhet som påslaget. */
    paslagPerioder: Record<Period, number | null>;
    /** När perioderna gäller, i en kort text. Tom med nättariffen ingen. */
    periodtext: string;
    /**
     * Skattefaktorn på spotpriset (b6): det som spotpriset multipliceras med, för elskatten och
     * momsen. 1 är av. `installd` är falskt när förvalet för elområdet gäller.
     */
    skattefaktor: { varde: number; forval: number; installd: boolean };
  } | null;
  /** Förvärmningens timmar (gränsen forvarmning.timmar), eller null för en båt. */
  forvarmning: {
    timmar: number;
    forval: number;
    installd: boolean;
    min: number;
    max: number;
  } | null;
  /** Värmemodellen under Avancerat, eller null för en båt. */
  varmemodell: { parametrar: Modellparameter[]; text: string } | null;
  /** Styrningen i molnet och vad den styr, eller null för en båt. */
  styrning: { lage: Styrningslage; vad: string[] } | null;
  nav: {
    sort: Navsort;
    skapad: number;
    senastHord: number | null;
    /** b7: paketets version som körs, och om den provas. Null: navet har ingen startdel. */
    version: string | null;
    prov: boolean;
    startdel: string | null;
    /** När navet senast uppdaterades, och en tillbakagång om en sådan har skett. */
    uppdaterad: number | null;
    tillbakagang: { fran: string; till: string; orsak: string; tid: number } | null;
  }[];
  /** b7: versionen som platsen är låst vid, eller null för den senaste. */
  navVersion: string | null;
  /** b8: golvet: inget paket under den versionen körs (BK20). */
  navGolv: string;
  /** b8: Ruuvi-givarna, eller null utan ett nav på en Pi. */
  givare: Givarinstallningar | null;
  /** b8: gränserna för varningen för hård vind, eller null för ett hus. */
  vind: { kulingMs: Gransinstallning; byarMs: Gransinstallning } | null;
  /** b11: gränserna för svartvattentanken, eller null för ett hus. */
  tankar?: { svartvattenVarning: Gransinstallning; svartvattenAkut: Gransinstallning } | null;
  /** b11 (BK31): kortet Badtemperatur visas på Hälsa. Förvalet är på. */
  badtemperatur?: boolean;
  /** b10: gränserna för kylskåpet och frysen, för alla platser. */
  kylFrys: {
    kylMaxGrader: Gransinstallning;
    kylMinuter: Gransinstallning;
    frysMaxGrader: Gransinstallning;
    frysMinuter: Gransinstallning;
  };
  /** b10: vinterläget, eller null för ett hus och för en båt utan nav ombord. */
  vinterlage: Vinterinstallning | null;
  /** b12: motorerna, dieseltanken, servicen och självsvängningen, eller null för ett hus. */
  motorer?: Motorinstallningar | null;
  /** b14: jordgivarna: namnen och gränserna, för ett hus. */
  jordgivare?: Jordgivarinstallning[] | null;
  /** b13: gränserna för misstänkt is i väderstationen, för alla platser. */
  vaderstation?: {
    isTempGrader: Gransinstallning;
    isTimmar: Gransinstallning;
    isPrognosMs: Gransinstallning;
  };
  /**
   * b20-2: ytterdörren: incheckningen, utcheckningen och när städningen är klar efter en egen
   * vistelse och efter gäster, som klockslag i timmar (17,5 är 17.30). Null för en båt.
   */
  ytterdorr?: {
    incheckning: Gransinstallning;
    utcheckning: Gransinstallning;
    stadEgen: Gransinstallning;
    stadGast: Gransinstallning;
  } | null;
  /** Navens sorter som platsen kan ha. */
  navsorter: Navsort[];
  /** b16: Victron: GX-enheten, bankerna och nycklarna. Null: platsen har inget nav. */
  victron?: Victroninstallningar | null;
  /** b17: Homey Pro: adressen, nyckeln och kontakten. Null: platsen har inget nav. */
  homey?: Homeyinstallningar | null;
  /**
   * b16: förbrukarna i ett hus, till exempel en luftvärmepump eller en kamin, med signalen för när
   * de går. En båt har dem i uppställningen (motorer). Null för en båt.
   */
  forbrukare?: Uppstallningsforbrukare[] | null;
  konton: { id: string; epost: string; roll: Roll; du: boolean }[];
  logg: Andringsrad[];
}

/** Det appen visar på Hälsa. Tolkningen görs i molnet. */
export interface Halsa {
  plats: Platsinfo;
  /** Samma som plats, för appar från b2. */
  bat: { id: string; namn: string };
  /** När navet senast hördes av, eller null. */
  navSenastHord: number | null;
  /** Senaste värdet per signalnyckel, till exempel "batteri.soc@forbrukning". */
  signaler: Record<string, Signalvarde>;
  tolkning: {
    generator: { lage: Generatorlage; text: string };
    mastervolt: {
      kontakt: boolean | null;
      saknas: string[];
      /** Fel i navets egen kod (felkod 01–04 från gränssnittet), eller null. */
      ramfel: string | null;
    };
    /** Husets läge, från hus.lage, nobo.lage eller larmet. */
    husLage: "hemma" | "borta" | null;
  };
  planer: Planvisning[];
  /** Båda spärrarna, eller null för en båt. */
  styrningslage: Styrningslage | null;
  /** Elen och värmemodellen, eller null för en båt (b4). */
  el: Elvisning | null;
  /** b8: de valda givarna. Deras hälsa står i signalerna givare.batteri och givare.signal. */
  givare: Valdgivare[];
  /** b8: vinden från väderdata, eller null för ett hus. */
  vind: Vadervind | null;
  /** b9: badtemperaturen, eller null om ingen källa finns. */
  badtemperatur: Badtemperatur | null;
  /**
   * b10: vinterläget, till exempel "på (65–80 %)", när det är på eller en laddare är av. Null
   * annars, och för ett hus.
   */
  vinterlage: string | null;
  /**
   * b11: platsen är ej i drift och navet har varit tyst längre än gränsen för tyst nav. Värdena
   * är de senast kända, från den här tiden, och bedöms inte. Null annars.
   */
  senastKanda?: number | null;
  /** b12: resorna, förbrukningen, räckvidden och servicen, eller null för ett hus. */
  farder?: Fardkort | null;
  /** b13: dieseltankarna med litrarna (tabellen eller linjärt), eller null för ett hus. */
  dieseltankar?: Dieseltank[] | null;
  /** b13: väderstationen vid platsen, uppmätt, bredvid prognosen. Null utan station. */
  vaderstation?: Vaderstation | null;
  /** b14: jordfukten per givare, för ett hus. Null för en båt, och tom utan givare. */
  jordfukt?: Jordgivare[] | null;
  /** b20-2: ytterdörrens lås (kortet Ytterdörr), eller null utan lås. */
  ytterdorr?: Ytterdorr | null;
}

/** b20-2: ytterdörrens lås på Hälsa (kortet Ytterdörr). Bara läsning: ingen knapp. */
export interface Ytterdorr {
  /** last, olast, regel eller okand. */
  lage: string;
  /** Dörren öppen, eller null om den inte är känd, eller om dörren inte läses (dorrLases). */
  oppen: boolean | null;
  /** b20-2-1: false när dörren är av på Pi:n (dorr.av): kortet säger "Dörren: läses inte". */
  dorrLases: boolean;
  /** Batteriet i procent, eller null. */
  batteri: number | null;
  /** Kontakten med låset: borta när låset inte har nåtts på 30 minuter. */
  kontakt: "ok" | "borta" | null;
  /** Den senaste ändringen, till exempel "Låst 11.04", med tiden. */
  senast: { text: string; tid: number } | null;
  /** Låsningar, upplåsningar och öppen dörr de senaste 7 dygnen, nyast först, bara med tid. */
  historik: { tid: number; text: string }[];
  /** Var låset läses från, till exempel "Läses via Home Assistant · prov". */
  kalla: string;
}

/** b20-2: typen av vistelse: gäster, eller ägarens egen. */
export const VISTELSETYPER = ["gast", "egen"] as const;
export type Vistelsetyp = (typeof VISTELSETYPER)[number];

/**
 * b20-2: en vistelse på platsen, med ankomst och avresa. Samma form som när vistelserna senare
 * kommer från en extern källa (b22, Hostaways mejl): id, start, slut, typ och status.
 */
export interface Vistelse {
  id: string;
  /** Ankomsten (incheckningen), ms. */
  start: number;
  /** Avresan (utcheckningen), ms. */
  slut: number;
  typ: Vistelsetyp;
  status: "bekraftad" | "avbokad";
  /** Var vistelsen kommer från: ägaren i appen. Senare också en extern källa. */
  kalla: string;
  /** När städningen ska vara klar, ms: avresedagen kl. städningen efter typen. */
  stadSlut: number;
}

/** b14: en jordgivare (Ecowitt, kanal 1–8) på Hälsa. */
export interface Jordgivare {
  /** Givarens kanal: kanal1–kanal8, som instansen i jord.fukt. */
  kanal: string;
  /** Namnet: ägarens i Båtkoll, annars Home Assistants, annars "Kanal 1". */
  namn: string;
  /** Namnet i Home Assistant, eller null. */
  haNamn: string | null;
  fukt: number | null;
  tid: number | null;
  batteri: { lagt: boolean | null; volt: number | null; procent: number | null };
  /** Timmedlen de senaste 7 dygnen, för kurvan. */
  kurva: { tid: number; fukt: number }[];
  /** Gränserna: torrt under och blött över, i %. */
  torr: number;
  blot: number;
  lage: "torr" | "blot" | "ok" | null;
}

/** b14: en jordgivares inställning: namnet och gränserna. */
export interface Jordgivarinstallning {
  kanal: string;
  /** Ägarens namn i Båtkoll, eller null. */
  namn: string | null;
  haNamn: string | null;
  torr: Gransinstallning;
  blot: Gransinstallning;
}

/** b13: väderstationen, uppmätt vid platsen, med prognosen för samma timme bredvid. */
export interface Vaderstation {
  /** Källan: "Ecowitt" med gatewayens modell, eller "BTHome". */
  kalla: string;
  /**
   * b16: varifrån temperaturen och fukten ute kommer, när navet säger det (ute.kalla), till
   * exempel "Home Assistant: sensor.ute_temperatur". Null: okänt, eller samma som stationen.
   */
  tempKalla?: string | null;
  fuktKalla?: string | null;
  /** Det senaste uppmätta värdet. */
  tid: number;
  temp: number | null;
  fukt: number | null;
  /** Vinden som visas: den uppmätta, eller prognosens vid misstänkt is. */
  vind: number | null;
  byvind: number | null;
  riktning: number | null;
  /** Vindens källa på kortet: uppmätt, eller prognosen vid misstänkt is. */
  vindkalla: "uppmatt" | "prognos";
  regn: number | null;
  regnDygn: number | null;
  regnar: boolean | null;
  tryck: number | null;
  ljus: number | null;
  uv: number | null;
  /** Misstänkt is: vindvärdena visas inte. Null annars. */
  is: { sedan: number; text: string } | null;
  /** Prognosen för timmen nu, eller null. */
  prognos: {
    kalla: string;
    tid: number;
    temp: number | null;
    vind: number | null;
    byvind: number | null;
    riktning: number | null;
  } | null;
  /** Givarna med lågt batteri, med namnet som stationen ger dem. */
  lagaBatterier: string[];
}

/** b10: de tre spärrarna för vinterläget. Navet skriver bara när alla tre är på. */
export interface Vintersparrar {
  /** Styrningen i molnet. */
  molnet: boolean;
  /** Styrningen på platsen (STYRNING på Pi:n), som navet senast sade den. Null: okänd. */
  platsen: boolean | null;
  /** Skrivningen på Pi:n (MASTERBUS_SKRIV, frågorna på, can1 ur listen-only). Null: okänd. */
  pi: boolean | null;
  /** Varför navet inte kan skriva, enligt navet, eller tom. */
  piOrsak: string;
}

/** b10: en rad i vitlistan i appen, med laddarens läge och den senaste skrivningen. */
export interface Vinterrad {
  rad: string;
  enhet: string;
  variabel: string;
  tab: number;
  index: number;
  /** Vad laddaren gör, till exempel "laddar förbrukningsbanken (MLi6000) från landströmmen". */
  gor: string;
  /** Batteriet (med BMS) som styr laddaren i fas 2. */
  batteri: string;
  /** Variabelns värde som navet senast läste: sant är på. */
  varde: { pa: boolean; tid: number } | null;
  /** Laddarens läge (laddare.lage), till exempel float eller av. */
  laddare: { text: string; tid: number } | null;
  /** Strömmen och laddningen i banken som laddaren laddar. */
  strom: { a: number; tid: number } | null;
  soc: { procent: number; tid: number } | null;
  /** Den senaste skrivningen från appen, eller null. */
  senaste: {
    tid: number;
    vem: string;
    pa: boolean;
    /** skickad, klar, fel, sparrad eller planerad. */
    status: string;
    resultat: string | null;
    tillbakalast: string | null;
  } | null;
  /**
   * b11: varför navet inte skriver till enheten, enligt navet: BusID och det navet hör på
   * bussen säger inte att det är just den enheten. Null när det stämmer, eller utan svar.
   */
  identitet?: string | null;
}

/** b11 (BK33): när styrningen i molnet stängs, slår navet på laddarna först. */
export interface Vinterstangning {
  /** Styrningen stängs sedan den här tiden, och navet har inte svarat än. Null annars. */
  sedan: number | null;
  /** Den senaste stängningen: om navet bekräftade att laddarna är på, och vad det svarade. */
  senaste: { tid: number; ok: boolean; text: string } | null;
}

/** b10: vinterläget under Inställningar, för en båt med ett nav ombord. */
export interface Vinterinstallning {
  sparrar: Vintersparrar;
  /** Alla tre spärrarna är på: appen visar knapparna. */
  kanSkriva: boolean;
  /** Fas 2, det automatiska vinterläget, är på i molnet. */
  pa: boolean;
  /** Navets läge, till exempel "på (65–80 %)", eller null om navet inte har sagt något. */
  lage: string | null;
  avProcent: number;
  paProcent: number;
  /** Varningen när banken är utanför de här gränserna medan vinterläget är på. */
  varningUnder: number;
  varningOver: number;
  rader: Vinterrad[];
  /** b11 (BK33): gränserna, som ägaren ändrar i appen. */
  granser?: {
    av: Gransinstallning;
    pa: Gransinstallning;
    varningUnder: Gransinstallning;
    varningOver: Gransinstallning;
  };
  /** b11 (BK33): stängningen av styrningen i molnet. */
  stangning?: Vinterstangning;
}

export interface Bat {
  id: string;
  namn: string;
}

// b8: Ruuvi-givarna och vinden från väderdata.

/**
 * Givarens plats. Båten: kabin, motorrum, ute och batteriutrymme. Hus: ute eller ett rum.
 * b9: kylskåp och frys för båda, och skrovet för en båt (mot skrovets insida, för
 * badtemperaturen).
 */
export type Givarroll =
  | "kabin"
  | "motorrum"
  | "ute"
  | "batterirum"
  | "rum"
  | "kyl"
  | "frys"
  | "skrov"
  | "vaderstation";

export const GIVARROLLER_BAT: readonly Givarroll[] = [
  "kabin",
  "motorrum",
  "ute",
  "batterirum",
  "kyl",
  "frys",
  "skrov",
  "vaderstation",
];
export const GIVARROLLER_HUS: readonly Givarroll[] = ["ute", "rum", "kyl", "frys", "vaderstation"];

export const GIVARROLLNAMN: Record<Givarroll, string> = {
  kabin: "Kabin",
  motorrum: "Motorrum",
  ute: "Ute",
  batterirum: "Batteriutrymme",
  rum: "Rum",
  kyl: "Kylskåp",
  frys: "Frys",
  skrov: "Skrovet",
  vaderstation: "Väderstationen",
};

/** En signal som en givare ger: namnet och instansen, till exempel hus.zon.temp@Loft. */
export interface Signalval {
  signal: string;
  instans: string | null;
}

/** Valet för en givare, som navet får: vilka signaler dess temperatur och fukt ger. */
export interface Givarval {
  id: string;
  temp: Signalval | null;
  fukt: Signalval | null;
  /**
   * b13: givaren är platsens väderstation (BTHome, Shellys WS90): vinden, regnet, trycket,
   * ljuset och UV-index blir signalerna vader.*. Ett nav som inte känner till fältet bryr sig
   * inte om det.
   */
  vader?: boolean;
}

/**
 * En givare som navet har hört, i listan till molnet. Ingen MAC-adress: id:t är navets eget, och
 * det korta id:t är de fyra sista tecknen i MAC-adressen, som i Ruuvis app.
 */
export interface Hordgivare {
  id: string;
  kort: string;
  modell: string | null;
  temp: number | null;
  fukt: number | null;
  signal: number | null;
  batteri: number | null;
  /** b9: batteriet i procent, för BTHome och Inkbird. */
  batteriProcent?: number | null;
  /** b9: krypterad BTHome. Utan nyckel på navet saknas värdena. */
  krypterad?: boolean;
  nyckelSaknas?: boolean;
  senast: number;
}

/** En vald givare och dess plats. Namnet gäller ett rum. */
export interface Valdgivare {
  id: string;
  kort: string;
  roll: Givarroll;
  namn: string | null;
  /** b9: modellen enligt navets senaste lista, om den finns. */
  modell?: string | null;
}

/** Inställningar → Givare, bara för ägare. */
export interface Givarinstallningar {
  /** Navets senaste lista, eller null om navet inte har skickat någon. */
  lista: Hordgivare[] | null;
  listaTid: number | null;
  /** Navet har Bluetooth, eller null om det inte har sagt något. */
  bluetooth: boolean | null;
  valda: Valdgivare[];
  roller: Givarroll[];
}

/**
 * En timme eller en observation av vinden från väderdata. Namnen vader.* skiljer den från
 * båtens givare (instrument.vind_*): vinden är inte uppmätt ombord.
 */
export interface Vadervarde {
  tid: number;
  "vader.vind_ms": number | null;
  "vader.byvind_ms": number | null;
  "vader.vind_riktning": number | null;
}

/** Vinden för en båt: prognosen för de närmaste 24 timmarna och den senaste observationen. */
export interface Vadervind {
  prognos: { kalla: string; hamtad: number | null; timmar: Vadervarde[] } | null;
  /** Från den närmaste aktiva stationen inom 30 km, eller null. */
  observation: (Vadervarde & { kalla: string; station: string; avstandKm: number }) | null;
  /** Gränserna för varningen för hård vind. */
  granser: { kulingMs: number; byarMs: number };
  /**
   * b11: positionen som väderdatan hämtas för: båtens GPS (färsk), platsens position, eller
   * navets senast kända GPS-position när platsen saknar en egen. Null: ingen position alls.
   */
  position?: "gps" | "platsen" | "senast_kanda" | null;
}

/** En gräns som ägaren kan ändra i appen, med förvalet. */
export interface Gransinstallning {
  varde: number;
  forval: number;
  installd: boolean;
  min: number;
  max: number;
}

/** b9: badtemperaturen, från den bästa källa som finns. */
export interface Badtemperatur {
  temp: number;
  tid: number;
  /**
   * Var den kommer ifrån: platsens egen givare (vatten.temp, på båten givaren i skrovet), givaren
   * mot skrovets insida (skrov.temp, en uppskattning), den närmaste mätstationen, eller (b11,
   * BK31) ett beräknat värde från satellit och modell, till exempel Copernicus Marine.
   */
  kalla: "givaren" | "skrovet" | "station" | "beraknad";
  /** Källan i ord, till exempel "SMHI: Marstrand, 0,5 km". */
  text: string;
  /** b11: källan som ska anges på kortet, för ett beräknat värde. */
  upphov?: string;
}

/**
 * b9, ändrad i b21-1 (PL6/PL7): givarens namn i appen, i loggen och som källa för Ute: märket och
 * fyra tecken ur navets HMAC (skilj-delen av givarens id, saltad och lokal), till exempel
 * "Ruuvi (a3f9)". Aldrig tecken ur MAC-adressen. Skiljet ligger i navets sparade fil och är
 * stabilt mellan omstarter. Namnet har parenteser, så att det aldrig matchar tvättmönstret
 * (MAC_SLUT_NAMN) som tar bort det gamla "Ruuvi A1B2". Utan modell är det en Ruuvi, som i b8.
 */
export function givarnamn(modell: string | null | undefined, id: string): string {
  const m = modell ?? "";
  const marke = m.startsWith("Shelly")
    ? "Shelly"
    : m.startsWith("Xiaomi")
      ? "Xiaomi"
      : m.startsWith("Inkbird")
        ? "Inkbird"
        : m === "BTHome"
          ? "BTHome"
          : "Ruuvi";
  const skilj = /^[0-9A-F]{4}-([0-9a-f]{4})$/.exec(id)?.[1];
  return skilj ? `${marke} (${skilj})` : marke;
}
